// @ts-strict-ignore
import * as React from 'react';
import ReactHotToast from 'react-hot-toast';

import { normalizeFile } from '@/utils/fileUtils';

import type { ReportConfigType } from './useFetchReportConfig';

const showErrorToast = (message: string) => {
  ReactHotToast.error(message, {
    duration: 3000,
    style: {
      maxWidth: '100%',
    },
  });
};

const validateFile = (
  file: File,
  config: ReportConfigType,
  handlers?: FileErrorHandlers
) => {
  if (config.file_types.length > 0 && !config.file_types.includes(file.type)) {
    const message = `File ${file.name} type: ${file.type} not allowed`;
    if (handlers?.onFileNotSupported) {
      handlers.onFileNotSupported(file, message);
    } else {
      showErrorToast(message);
    }
    return false;
  }

  if (file.size > config.file_size_limit) {
    const message = `File ${file.name} size exceeds the limit of ${
      config.file_size_limit / (1024 * 1024)
    } MB`;
    if (handlers?.onSizeLimitExceed) {
      handlers.onSizeLimitExceed(file, message);
    } else {
      showErrorToast(message);
    }
    return false;
  }

  const sanitizedFileName = file.name.toLowerCase().endsWith('.pdf')
    ? file.name.slice(0, -4)
    : file.name;

  if (
    config.file_name_suffixes.length > 0 &&
    !config.file_name_suffixes.some((suffix) =>
      sanitizedFileName.endsWith(suffix)
    )
  ) {
    const message = `File ${
      file.name
    } name should end with one of the following suffixes: ${config.file_name_suffixes.join(
      ', '
    )}`;
    if (handlers?.onFileNameSuffixMismatch) {
      handlers.onFileNameSuffixMismatch(file, message);
    } else {
      showErrorToast(message);
    }
    return false;
  }

  return true;
};

const readFolderContents = (
  entry: FileSystemDirectoryEntry | FileSystemEntry | FileSystemFileEntry,
  parentFolderPath: string,
  onFilesDrop: (files: File[]) => void
) => {
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    reader.readEntries(
      (entries: (FileSystemDirectoryEntry | FileSystemFileEntry)[]) => {
        entries.forEach((subEntry) => {
          const newFolderPath = parentFolderPath
            ? `${parentFolderPath}/${entry.name}`
            : entry.name;
          readFolderContents(subEntry, newFolderPath, onFilesDrop);
        });
      }
    );
  } else if (entry.isFile) {
    (entry as FileSystemFileEntry).file((file: File) => {
      file = normalizeFile(file);
      file['relativePath'] = parentFolderPath + '/' + file.name;
      onFilesDrop([file]);
    });
  }
};

export const useFileHandler = (
  onFilesDrop: (files: File[]) => void,
  config: ReportConfigType,
  handlers?: FileErrorHandlers
) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const folderInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const item = e.dataTransfer.items[i];
        if (item.kind === 'file') {
          if (item.webkitGetAsEntry && item.webkitGetAsEntry().isDirectory) {
            if (!config.allow_folder_upload) {
              const message = 'Folder upload is not allowed';
              if (handlers?.onFolderUploadNotAllowed) {
                handlers.onFolderUploadNotAllowed(message);
              } else {
                showErrorToast(message);
              }
              continue;
            }
            const folderEntry = item.webkitGetAsEntry();
            readFolderContents(folderEntry, '', onFilesDrop);
          } else {
            let file = item.getAsFile() as File;
            file = normalizeFile(file);
            if (!validateFile(file, config, handlers)) {
              continue;
            }
            onFilesDrop([file]);
          }
        }
      }
    } else {
      const files: File[] = Array.from(e.dataTransfer.files).map(normalizeFile);
      const validFiles = files.filter((file) =>
        validateFile(file, config, handlers)
      );
      onFilesDrop(validFiles);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).map(normalizeFile);
      const validFiles = files.filter((file) =>
        validateFile(file, config, handlers)
      );
      onFilesDrop(validFiles);
      e.target.value = '';
    }
  };

  const handleFileInputClick = (type: 'file' | 'folder') => {
    if (type === 'file' && fileInputRef.current) {
      fileInputRef.current.click();
    } else if (type === 'folder' && folderInputRef.current) {
      folderInputRef.current.click();
    }
  };

  return {
    isDragging,
    fileInputRef,
    folderInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileSelect,
    handleFileInputClick,
  };
};

export type FileErrorHandlers = {
  onSizeLimitExceed?: (file: File, message: string) => void;
  onFileNotSupported?: (file: File, message: string) => void;
  onFileNameSuffixMismatch?: (file: File, message: string) => void;
  onFolderUploadNotAllowed?: (message: string) => void;
};
